import { Secp256k1Wallet } from '@cosmjs/amino';
import type { OfflineSigner } from '@cosmjs/proto-signing';
import { DirectSecp256k1Wallet } from '@cosmjs/proto-signing';
import {
  WALLET_ADAPTERS,
  CHAIN_NAMESPACES,
  SafeEventEmitterProvider,
} from '@web3auth/base';
import { CommonPrivateKeyProvider } from '@web3auth/base-provider';
import { Web3AuthNoModal } from '@web3auth/no-modal';
import { OpenloginAdapter } from '@web3auth/openlogin-adapter';

import { APP_CONFIG } from 'app-config';
import { BaseAuthProvider } from 'app/services/Authentication/base/BaseAuthProvider';

import { COSMOS_CHAINS } from 'chains/cosmos/cosmos.assets';
import { CosmosChainId } from 'chains/cosmos/cosmos.types';

import {
  Web3AuthProviderOptions,
  WalletProviderInterface,
  Web3AuthLoginProvider,
} from '../../../types/providers';

const {BASE_URL} = APP_CONFIG;

const LAST_USED_LOGIN_PROVIDER_KEY = 'last_used_login_provider';

export class Web3AuthProvider
  extends BaseAuthProvider
  implements WalletProviderInterface<Web3AuthProviderOptions>
{
  private _provider: SafeEventEmitterProvider = null!;

  private _privateKey: Buffer = null!;

  web3Auth: Web3AuthNoModal | null = null;

  constructor() {
    super('web3_auth');
  }

  async init() {
    const chain = this.elysChain;

    const chainConfig = {
      chainNamespace: CHAIN_NAMESPACES.OTHER,
      rpcTarget: chain.rpcURL,
      displayName: 'Elys DEX',
      blockExplorer: chain.explorerURL.transaction,
      chainId: chain.chainId,
      ticker: chain.currencies[0].coinMinimalDenom,
      tickerName: chain.currencies[0].coinDenom,
    };

    const web3Auth = new Web3AuthNoModal({
      chainConfig,
      clientId: APP_CONFIG.WEB3.clientId,
      web3AuthNetwork:
        this.elysChain.chainNetwork === 'mainnet'
          ? 'sapphire_mainnet'
          : 'testnet',
    });

    const privateKeyProvider = new CommonPrivateKeyProvider({
      config: {
        chainConfig,
      },
    });

    const openloginAdapter = new OpenloginAdapter({
      adapterSettings: {
        uxMode: 'popup',
        whiteLabel: {
          appName: chain.chainName,
          appUrl: BASE_URL,
          logoLight: `${BASE_URL}/logos/with-text.png`,
          logoDark: `${BASE_URL}/logos/with-text.png`,
          theme: {
            primary: '#000000',
          },
        },
      },
      privateKeyProvider,
    });

    web3Auth.configureAdapter(openloginAdapter);

    await web3Auth.init();
    this.web3Auth = web3Auth;
  }

  async connect(sessionCheck?: boolean, options?: Web3AuthProviderOptions) {
    this.loginProvider =
      options?.loginProvider ||
      (localStorage.getItem(
        LAST_USED_LOGIN_PROVIDER_KEY,
      ) as Web3AuthLoginProvider);

    const web3Auth = this.web3Auth;

    if (!web3Auth) {
      throw new Error('Web3Auth not initialized');
    }

    // check options only if sessionCheck is not valid
    if (!sessionCheck && !options) {
      throw new Error('Missing options');
    }

    let provider = this.web3Auth?.provider;

    // try to connect to the provider if sessionCheck is false
    if (!sessionCheck) {
      // logout if user is already connected
      if (web3Auth.connected) {
        await web3Auth.logout();
      }

      provider = await this.web3Auth?.connectTo(WALLET_ADAPTERS.OPENLOGIN, {
        loginProvider: options?.loginProvider,
        extraLoginOptions: {
          login_hint: options?.username,
        },
      });
    }

    if (!web3Auth.connected) return;

    try {
      this._provider = provider!;

      this._privateKey = await this.getPrivateKey();
      this.offlineSigner = await DirectSecp256k1Wallet.fromKey(
        this._privateKey,
        this.elysChain.addressPrefix,
      );

      await this.refreshAccount();

      this.connected = true;

      // save last used login provider
      localStorage.setItem(LAST_USED_LOGIN_PROVIDER_KEY, this.loginProvider);
    } catch (e) {
      // clean up
      this.web3Auth = null;
      this._provider = null!;
      this._privateKey = null!;

      if (sessionCheck) {
        return;
      }

      localStorage.removeItem(LAST_USED_LOGIN_PROVIDER_KEY);

      throw e;
    }
  }

  async disconnect() {
    try {
      await this.web3Auth?.logout();

      this.web3Auth = null;
      this._provider = null!;
      this._privateKey = null!;

      super.disconnect();

      localStorage.removeItem(LAST_USED_LOGIN_PROVIDER_KEY);
    } catch (e) {
      console.warn(e);

      throw new Error('Error disconnecting from Web3Auth.');
    }
  }

  async getPrivateKey(): Promise<Buffer> {
    const key = (await this._provider.request({
      method: 'private_key',
    })) as string;

    return Buffer.from(key, 'hex');
  }

  async resolveAddress(chainId: CosmosChainId): Promise<string> {
    const signer = await this.getOfflineSigner(chainId);

    return (await signer.getAccounts())[0].address;
  }

  async getOfflineSigner(chainId: CosmosChainId) {
    if (this.elysChain.chainId === chainId && this.offlineSigner) {
      return this.offlineSigner;
    }

    const {addressPrefix} = COSMOS_CHAINS[chainId];

    const offlineSigner = await DirectSecp256k1Wallet.fromKey(
      this._privateKey,
      addressPrefix,
    );

    return offlineSigner;
  }

  async getOfflineSignerAmino(chainId: CosmosChainId) {
    const {addressPrefix} = COSMOS_CHAINS[chainId];

    const offlineSigner = await Secp256k1Wallet.fromKey(
      this._privateKey,
      addressPrefix,
    );

    return offlineSigner;
  }
  //@ts-expect-error - todo: fix this
  async createSigningStargateClient(
    chainId: CosmosChainId,
    offlineSigner?: OfflineSigner,
  ) {
    offlineSigner = offlineSigner ?? (await this.getOfflineSigner(chainId));

    return super.createSigningStargateClient(chainId, offlineSigner);
  }
}
